/**
 * Las cifras económicas de la pestaña «Resumen», con definiciones precisas (acuerdo del 22-10-2026):
 *
 *   · FACTURADO y GASTADO — del periodo del filtro, en BASE IMPONIBLE (lo que cuadra con la cuenta
 *     70501 y el grupo 6), con el IVA y el total debajo. La inversión comprometida (el valor de las
 *     oportunidades En Inversión) NO es un gasto: va en una línea aparte, rotulada ESTIMADO.
 *   · PENDIENTE DE EMITIR, DE COBRO y DE PAGO — saldos A HOY: no obedecen al filtro de periodo y se
 *     rotulan «a hoy». El pendiente de emitir cuenta los borradores (antes no contaban en ninguna
 *     cifra) y los hitos ya vencidos; lo planificado va aparte.
 */
import { Coins, FileText, Receipt, TrendingDown, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { DashboardOverviewResponse } from '@jr2os/shared'
import { formatCents } from '../../lib/money'
import { StatBox } from './StatBox'

export default function EconomicsSummary({
  economics,
}: {
  economics: DashboardOverviewResponse['economics']
}) {
  const { t } = useTranslation('dashboard')
  const d = economics.detail
  const money = (n: number) => formatCents(n)
  const issueCents =
    d.pendingIssue.drafts.baseCents +
    d.pendingIssue.milestonesDue.baseCents +
    d.pendingIssue.recurringDue.baseCents
  const hints: string[] = []
  if (d.pendingIssue.drafts.count > 0)
    hints.push(
      t('overview.eco.issueDrafts', {
        count: d.pendingIssue.drafts.count,
        value: money(d.pendingIssue.drafts.baseCents),
      })
    )
  if (d.pendingIssue.milestonesDue.count > 0)
    hints.push(
      t('overview.eco.issueMilestones', {
        count: d.pendingIssue.milestonesDue.count,
        value: money(d.pendingIssue.milestonesDue.baseCents),
      })
    )
  if (d.pendingIssue.recurringDue.count > 0)
    hints.push(
      t('overview.eco.issueRecurring', {
        count: d.pendingIssue.recurringDue.count,
        value: money(d.pendingIssue.recurringDue.baseCents),
      })
    )
  if (d.pendingIssue.milestonesPlanned.count > 0)
    hints.push(
      t('overview.eco.issuePlanned', { value: money(d.pendingIssue.milestonesPlanned.baseCents) })
    )

  const Heading = ({ children }: { children: string }) => (
    <h3 className="text-[11px] uppercase tracking-wider t-muted">{children}</h3>
  )

  return (
    <section className="space-y-4">
      <div className="space-y-2">
        <Heading>{t('overview.eco.groupPeriod')}</Heading>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <StatBox
            label={t('overview.eco.billed')}
            value={money(d.billed.baseCents)}
            icon={Receipt}
            accent="jr2"
            hint={t('overview.eco.billedHint', {
              vat: money(d.billed.vatCents),
              total: money(d.billed.totalCents),
            })}
          />
          <StatBox
            label={t('overview.eco.spent')}
            value={money(d.spent.baseCents)}
            icon={TrendingDown}
            accent="rose"
            hint={t('overview.eco.spentHint', {
              vat: money(d.spent.vatCents),
              total: money(d.spent.totalCents),
            })}
          />
          {economics.investedCents > 0 && (
            <p className="text-xs t-muted self-center px-1">
              {t('overview.eco.investedLine', { value: money(economics.investedCents) })}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Heading>{t('overview.eco.atToday')}</Heading>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <StatBox
            label={t('overview.eco.pendingIssue')}
            value={money(issueCents)}
            icon={FileText}
            accent="amber"
            hint={hints.length > 0 ? hints.join(' · ') : t('overview.eco.nothing')}
          />
          <StatBox
            label={t('overview.eco.pendingCollect')}
            value={money(d.pendingCollect.totalCents)}
            icon={Wallet}
            accent="blue"
            hint={t('overview.eco.collectHint', {
              overdue: money(d.pendingCollect.overdueCents),
              period: money(d.pendingCollect.ofPeriodCents),
            })}
          />
          <StatBox
            label={t('overview.eco.pendingPay')}
            value={money(d.pendingPay.totalCents)}
            icon={Coins}
            accent="emerald"
            hint={t('overview.eco.payHint', { count: d.pendingPay.count })}
          />
        </div>
      </div>
    </section>
  )
}
